import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, CheckCircle2, Loader2, CalendarCheck, ShieldCheck, Users, Sparkles } from "lucide-react";
import { Logo } from "../components/Logo";

// Unauthenticated client — same pattern as AutoInterview's publicApi.
// No base-URL fallback (see project auth constraints).
const publicApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });

const BENEFITS = [
  { icon: Sparkles, text: "See AI screening run on your own roles" },
  { icon: ShieldCheck, text: "Walk through integrity & compliance controls" },
  { icon: Users, text: "Get your team set up with the right plan" },
];

const inputCls =
  "w-full rounded-xl border border-border px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#0025D8] focus:ring-2 focus:ring-[#0025D8]/20 transition-all";

export default function BookDemo() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [form, setForm] = useState({ name: "", work_email: "", company: "", phone: "", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.company.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, company, and phone.");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.work_email.trim())) {
      toast.error("Please enter a valid work email.");
      return;
    }
    setBusy(true);
    try {
      await publicApi.post("/public/book-demo", form);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit your request — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="bg-white text-foreground">
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#F4F7FB]">
        <div
          className="pointer-events-none absolute -top-32 -right-32 w-[42rem] h-[42rem] rounded-full opacity-60"
          style={{ background: "radial-gradient(circle, rgba(0,37,216,0.10) 0%, rgba(124,58,237,0.06) 40%, transparent 70%)" }}
        />
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          {/* Left — value prop */}
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-[#0025D8]/15 shadow-sm px-3.5 py-1.5 rounded-full mb-7">
              <CalendarCheck className="w-3.5 h-3.5 text-[#0025D8]" />
              <span className="font-mono text-[11px] text-[#0025D8] font-semibold tracking-wider">BOOK A DEMO</span>
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.05] mb-5 text-[#0F172A]">
              See VetraHub on<br /><span className="text-[#0025D8]">your own roles.</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8 max-w-md">
              Tell us a little about your hiring and we'll set up a walkthrough tailored to your team. It takes about 20 minutes.
            </p>
            <ul className="space-y-3">
              {BENEFITS.map((b) => (
                <li key={b.text} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#0025D8]/10 flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-4 h-4 text-[#0025D8]" />
                  </div>
                  <span className="text-sm text-[#0F172A] pt-1.5">{b.text}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right — form or success */}
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-tr from-[#0025D8]/10 to-[#7C3AED]/10 rounded-3xl blur-2xl" />
            <div className="relative rounded-2xl border border-border bg-white shadow-2xl shadow-[#0F172A]/10 p-6 md:p-8">
              {done ? (
                <div className="text-center py-8">
                  <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 flex items-center justify-center mx-auto mb-5">
                    <CheckCircle2 className="w-7 h-7 text-[#10B981]" />
                  </div>
                  <h2 className="font-display font-black text-2xl text-[#0F172A] mb-2">Request received.</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto mb-7">
                    Thanks — our team will reach out shortly to set up your demo.
                  </p>
                  <Link
                    to="/signup?role=company"
                    className="inline-flex items-center gap-2 bg-[#0025D8] hover:bg-[#001CB3] text-white px-6 py-3 rounded-xl font-semibold text-sm transition-colors"
                  >
                    Start free while you wait <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Name *</label>
                      <input type="text" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} required />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Phone *</label>
                      <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" className={inputCls} required />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Work email *</label>
                    <input type="email" value={form.work_email} onChange={set("work_email")} placeholder="you@company.com" className={inputCls} required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">Company *</label>
                    <input type="text" value={form.company} onChange={set("company")} placeholder="Your company" className={inputCls} required />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-muted-foreground mb-1.5">What are you hoping to solve? <span className="font-normal text-muted-foreground/60">(optional)</span></label>
                    <textarea value={form.message} onChange={set("message")} rows={3} placeholder="A bit about your hiring needs…" className={`${inputCls} resize-none`} />
                  </div>

                  {/* Honeypot — hidden from users, catches bots. */}
                  <div aria-hidden="true" className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden">
                    <label>Website<input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} /></label>
                  </div>

                  <button
                    type="submit"
                    disabled={busy}
                    className="w-full flex items-center justify-center gap-2 bg-[#0025D8] hover:bg-[#001CB3] text-white px-6 py-3.5 rounded-xl font-semibold text-sm shadow-lg shadow-[#0025D8]/20 transition-colors disabled:opacity-60"
                  >
                    {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request a demo <ArrowUpRight className="w-4 h-4" /></>}
                  </button>
                  <p className="text-[11px] text-muted-foreground text-center">
                    Prefer to explore first? <Link to="/signup?role=company" className="text-[#0025D8] font-semibold hover:underline">Start free →</Link>
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Slim footer */}
      <footer className="bg-white border-t border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-auto" />
            <span className="font-mono text-[10px] text-muted-foreground">© 2026</span>
          </div>
          <div className="flex gap-5">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Log in</Link>
            <Link to="/signup?role=company" className="text-sm text-[#0025D8] font-semibold hover:underline">Sign up free →</Link>
          </div>
        </div>
      </footer>
    </main>
  );
}
